import { TimeoutService } from '@df/ng/timeout.service';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { App } from '@df/core/app';

interface InvocaWindow extends Window {
    Invoca?: { PNAPI?: { run?: () => void } };
}

/**
 * Invoca (call tracking) swaps the static phone number for a tracking number
 * by scanning the DOM once. Anything that inserts phone-number markup after
 * that scan leaves the static number un-swapped, because Invoca never
 * revisits it: async iCMS header/footer content on first render, `*ifSize`
 * (which extends `NgIf`, so a breakpoint change destroys and recreates the
 * nodes), route changes, the promobar Swiper carousel (which clones nodes on
 * every loop), and `@defer` hydration of `page-off-canvas`.
 *
 * Rather than chase each of those triggers individually, watch the DOM for
 * new `tel:` links and re-run `Invoca.PNAPI.run()` - Invoca's documented
 * single-page-app re-scan hook, which is idempotent within a session.
 * See ASR-418.
 */
@Injectable({
    providedIn: 'root'
})
export class InvocaRescanService {
    private readonly app = inject(App);
    private readonly document = inject<Document>(DOCUMENT);
    private readonly timeoutService = inject(TimeoutService);
    private readonly destroyRef = inject(DestroyRef);

    private readonly seen = new WeakSet<Element>();

    private readonly rescan = this.timeoutService.debounce(() => {
        const invocaWindow = this.app.window as unknown as InvocaWindow;
        const run = invocaWindow.Invoca?.PNAPI?.run;
        if (typeof run !== 'function') {
            return;
        }
        run();
        this.document.querySelectorAll('a[href^="tel:"]').forEach(anchor => this.seen.add(anchor));
    }, 250);

    constructor() {
        if (this.app.isPlatformBrowser) {
            const observer = new MutationObserver(() => this.check());
            observer.observe(this.document.body, { childList: true, subtree: true });
            this.destroyRef.onDestroy(() => observer.disconnect());
        }
    }

    private check(): void {
        const anchors = this.document.querySelectorAll('a[href^="tel:"]');
        if ([...anchors].every(anchor => this.seen.has(anchor))) {
            return;
        }
        this.rescan();
    }
}
